"use client";

import { type ComponentPropsWithRef, Suspense } from "react";

import { useTranslations } from "next-intl";

import { PrimRadioButtons } from "@/shared-web.ui/prim.radio-buttons";
import { Text } from "@/shared-web.ui/ui.text";
import { useVariantState } from "@/stf.m.product/feat.product-detail";

import {
  type ServerDataOutput,
  extractFrequencyPerMonth,
  useSubscriptionSelectorActions,
} from "../data.subscription-data";
import * as SelectorContainer from "../prim.selector-container";

export type FrequencyCardProps = {
  frequencyPerMonth: number;
};
export const FrequencyCard = (props: FrequencyCardProps) => {
  const { frequencyPerMonth } = props;

  const t = useTranslations("stf_m_subscription.screen_subscription.frequency_selector");

  return (
    <div className="grid p-2">
      <Text size="4" weight="medium">
        {t.rich("shipment_frequency", {
          frequencyPerMonth,
          shipment: (chunks) => {
            return (
              <Text as="span" size="3" color="gray" ml="1" weight="regular">
                {chunks}
              </Text>
            );
          },
        })}
      </Text>
      <Text size="1" color="gray">
        {t("per_month")}
      </Text>
    </div>
  );
};

interface FrequencySelectorDisplayProps
  extends Pick<ComponentPropsWithRef<typeof PrimRadioButtons.Root>, "onValueChange" | "value"> {
  data: ServerDataOutput;
}
const FrequencySelectorDisplay = (props: FrequencySelectorDisplayProps) => {
  const { value, onValueChange, data } = props;
  const {
    subscriptionVariant: { frequencyPerMonthVariant },
  } = data;
  const { variantOptionsArray, variantOptions } = frequencyPerMonthVariant;

  const t = useTranslations("stf_m_subscription.screen_subscription.frequency_selector");

  return (
    <SelectorContainer.Root>
      <SelectorContainer.Title>
        {t("set_your_shipment_frequency")}
      </SelectorContainer.Title>
      <SelectorContainer.Body>
        <PrimRadioButtons.Root
          className="grid grid-cols-2"
          value={value}
          onValueChange={onValueChange}
        >
          {variantOptionsArray.map(({ optionKey }) => {
            const options = variantOptions[optionKey];
            const frequencyPerMonthValue = extractFrequencyPerMonth(options.optionName);
            return (
              <PrimRadioButtons.Item value={optionKey} key={optionKey}>
                <FrequencyCard frequencyPerMonth={frequencyPerMonthValue} />
              </PrimRadioButtons.Item>
            );
          })}
        </PrimRadioButtons.Root>
      </SelectorContainer.Body>
      <SelectorContainer.Remarks>
        {t("shipout_every_1st_and_15th_of_the_month")}
      </SelectorContainer.Remarks>
    </SelectorContainer.Root>
  );
};

interface FrequencySelectorWithParamsProps {
  data: ServerDataOutput;
}
const FrequencySelectorWithParams = (props: FrequencySelectorWithParamsProps) => {
  const { data } = props;
  const { opts } = useVariantState();
  const { setVal } = useSubscriptionSelectorActions({ data });
  const {
    subscriptionData: {
      subscriptionMeta: { variantMapper },
    },
  } = data;

  return (
    <FrequencySelectorDisplay
      data={data}
      value={opts?.[variantMapper.frequencyPerMonth]}
      onValueChange={(v) => {
        setVal("frequencyPerMonth", v);
      }}
    />
  );
};

interface FrequencySelectorProps {
  data: ServerDataOutput;
}
export const FrequencySelector = (props: FrequencySelectorProps) => {
  const { data } = props;
  return (
    <Suspense fallback={<FrequencySelectorDisplay data={data} />}>
      <FrequencySelectorWithParams data={data} />
    </Suspense>
  );
};
